// War archive: a durable, append-only record of every war, kept apart from
// the live `games/{id}` document (which is overwritten round by round and
// deleted when a player leaves).
//
//   warArchive/{bucket}/{gameId}/meta                 written at join
//   warArchive/{bucket}/{gameId}/initialHomefronts/p1 written at lock-in
//   warArchive/{bucket}/{gameId}/rounds/{n}           written with each round's persist
//                                                     (Hives compact: archive/hiveCodec.js)
//   warArchive/{bucket}/{gameId}/result               written at war end / abandon
//
// bucket is 'real' or 'test' (see testMode.js) and is frozen on the live
// doc as `archiveBucket` at join, so every later writer finds the path
// without recomputing. A live doc with no archiveBucket (a war started
// before this existed) is simply not archived.
//
// Every write here is best-effort: the archive must never be able to break
// the game, so writers catch and log instead of throwing, and each is its
// own set() rather than a leg of the live doc's update.

import { db } from '../FirebaseConfig'
import { ref, get, set } from 'firebase/database'
import { FORT_STRENGTH, BATTLEFIELD_COUNT } from '../rules/gameConfig'
import { structureShape } from '../rules/homefrontMaker'
import { HIVE_CODEC, compactRoundHives } from '../archive/hiveCodec'

export const ARCHIVE_ROOT = 'warArchive'
export const BUCKETS = ['real', 'test']

export function bucketFor(isTest) { return isTest ? 'test' : 'real' }

// `warArchive/{bucket}/{id}` for a live game doc (raw or parsed), or null
// when the war predates archiving.
export function archivePath(gameData) {
    if (!gameData?.id || !gameData?.archiveBucket) return null
    return `${ARCHIVE_ROOT}/${gameData.archiveBucket}/${gameData.id}`
}

// ── Compact clash notation ───────────────────────────────────────────────────

const SHAPE_WORD = { single: 'single', column: 'tall', rise: 'rise', fall: 'fall', vee: 'vee' }

// 'U-3:rise' — a destroyed structure's id and the shape read off its footprint.
export function structureLabel(hf, id) {
    const st = hf?.structures?.[id]
    const shape = st ? structureShape(st) : null
    return shape ? `${id}:${SHAPE_WORD[shape] ?? shape}` : id
}

// Base ledger at clash time → ['3', '2', 'c']: a mature Base by its live
// hit points, a construction as 'c'.
function ledgerBases(ledger) {
    return (ledger?.bases ?? []).map(b => (b?.mature ? String(b.hp ?? FORT_STRENGTH) : 'c'))
}

// One enriched reveal event (self perspective) → one absolute (p1/p2) clash
// record. Presentation-only fields (staged homefronts, grafted Bases) are
// dropped; what remains is what the engine decided and why.
export function compactClash(event, col, isSelfPlayer1) {
    const selfSide = isSelfPlayer1 ? 'p1' : 'p2'
    const oppSide  = isSelfPlayer1 ? 'p2' : 'p1'
    const out = { c: col + 1 }
    if (!event) return out
    out.p1 = isSelfPlayer1 ? (event.selfStrength ?? 0) : (event.oppStrength ?? 0)
    out.p2 = isSelfPlayer1 ? (event.oppStrength ?? 0) : (event.selfStrength ?? 0)
    out.ctx = event.context === 'both_attacked' ? 'both'
            : event.context === 'self_attacked' ? selfSide
            : event.context === 'opp_attacked'  ? oppSide
            : null
    out.win = event.type === 'WIN' ? selfSide : event.type === 'LOSS' ? oppSide : event.type === 'TIE' ? 'tie' : null

    if (event.aimS != null) {
        const hitSide = event.killSide === 'opp' ? oppSide : selfSide
        const hitHf   = event.killSide === 'opp' ? event.postClashOppHf : event.postClashSelfHf
        out.aim = `${hitSide}:${event.aimS}`
        out.retaliation = event.retaliationRow != null
        out.kills = event.kills ?? 0
        out.absorbed = event.absorbed ?? 0
        out.washed = !!event.washedByRockets
        const killed = (event.destroyedStructures ?? []).map(id => structureLabel(hitHf, id))
        const basesKilled = event.destroyedBroods ?? []
        if (killed.length) out.killed = killed
        if (basesKilled.length) out.basesKilled = basesKilled
    }

    const selfBases = ledgerBases(event.selfLedgerAtClash)
    const oppBases  = ledgerBases(event.oppLedgerAtClash)
    if (selfBases.length || oppBases.length) {
        out.bases = {}
        if (isSelfPlayer1) { if (selfBases.length) out.bases.p1 = selfBases; if (oppBases.length) out.bases.p2 = oppBases }
        else               { if (oppBases.length)  out.bases.p1 = oppBases;  if (selfBases.length) out.bases.p2 = selfBases }
    }
    return out
}

export function compactClashes(events, isSelfPlayer1) {
    const out = []
    for (let col = 0; col < BATTLEFIELD_COUNT; col++) out.push(compactClash(events?.[col], col, isSelfPlayer1))
    return out
}

// "R3  c1 P1 4v2 → crater P2@37 ×1 (U-3 wide)  ·  c2 P2 3v0 → crater P1@112  ·  c3 —"
export function roundLog(round, clashes) {
    const parts = clashes.map(k => {
        if (!k.win) return `c${k.c} —`
        const up = s => s.toUpperCase()
        let s
        if (k.win === 'tie') s = `c${k.c} tie ${k.p1}v${k.p2}`
        else {
            const w = k.win, l = w === 'p1' ? 'p2' : 'p1'
            s = `c${k.c} ${up(w)} ${k[w]}v${k[l]}`
        }
        if (k.aim) {
            const [side, cell] = k.aim.split(':')
            s += k.washed ? ` → washed at ${up(side)}@${cell}` : ` → crater ${up(side)}@${cell}`
            if (k.kills) s += ` ×${k.kills}`
            if (k.killed?.length) s += ` (${k.killed.map(x => x.replace(':', ' ')).join(', ')})`
            if (k.basesKilled?.length) s += ` +${k.basesKilled.length} base`
            if (k.retaliation) s += ' (retaliation)'
        }
        return s
    })
    return `R${round}  ${parts.join('  ·  ')}`
}

// ── Round entry ──────────────────────────────────────────────────────────────

// RTDB rejects `undefined` anywhere in a value; nulls are dropped silently.
function clean(obj) {
    return JSON.parse(JSON.stringify(obj, (_k, v) => (v === undefined ? null : v)))
}

function sideDeclarations(cur, side) {
    return {
        board:          cur[`${side}State`] ?? null,           // the board as submitted (pre-battle)
        targets:        cur[`${side}TargetPlots`] ?? null,
        targetSubplots: cur[`${side}TargetSubplots`] ?? null,
        dropZones:      cur[`${side}DropZones`] ?? null,
        drones:         cur[`${side}DroneStrikes`] ?? null,
        actions:        cur[`${side}RoundActions`] ?? null,
        baseBuilds:     cur[`${side}BaseBuilds`] ?? null,
        yield:          cur[`${side}Yield`] ?? 0,
    }
}

// The side's Hives before (rockets bought, Bases not yet baked in) and after
// the round, compact (archive/hiveCodec.js).
function sideHives(cur, updates, side) {
    return compactRoundHives(cur[`${side}Homefront`] ?? null, updates[`${side}Homefront`] ?? null)
}

// The archive record for one resolved round.
//   cur      the live doc snapshot the persist read (declarations + pre-round state)
//   res      resolvedEventsRef payload (events, round, isSelfPlayer1, post-crater hfs, kill counts, ...)
//   updates  the persist's write (post-battle boards, post-crater homefronts, scores)
// The live doc's round counter has already been bumped by the submits when a
// round resolves (res.round is 2 while round 1's clashes are litigated), so
// the archive numbers the round that was FOUGHT and keeps the counter as
// resolvedAtRound. The Hives are stored compact (hiveCodec); expandRoundEntry
// gives back hfBefore / hfAfter in full.
export function buildRoundEntry({ cur, res, updates, now = Date.now() }) {
    const isP1 = !!res.isSelfPlayer1
    const fought = res.round - 1
    const clashes = compactClashes(res.events, isP1)
    const toSides = (pair) => pair ? (isP1 ? { p1: pair.self ?? [], p2: pair.opp ?? [] } : { p1: pair.opp ?? [], p2: pair.self ?? [] }) : null
    const entry = {
        round: fought,
        resolvedAtRound: res.round,
        persistedAt: now,
        hiveCodec: HIVE_CODEC,
        p1: {
            ...sideDeclarations(cur, 'p1'),
            ...sideHives(cur, updates, 'p1'),
            boardAfter: updates.p1State ?? null,
            structuresLost: updates.p1StructuresLost ?? 0,
            score: updates.player1Score ?? 0,
        },
        p2: {
            ...sideDeclarations(cur, 'p2'),
            ...sideHives(cur, updates, 'p2'),
            boardAfter: updates.p2State ?? null,
            structuresLost: updates.p2StructuresLost ?? 0,
            score: updates.player2Score ?? 0,
        },
        clashes,
        log: roundLog(fought, clashes),
        resupply: toSides(res.resupply),
        scans: toSides(res.scans),
    }
    return clean(entry)
}

// ── Writers (best-effort) ────────────────────────────────────────────────────

async function safeSet(path, value, { onlyIfMissing = false } = {}) {
    try {
        const r = ref(db, path)
        if (onlyIfMissing) {
            const snap = await get(r)
            if (snap.exists()) return false
        }
        await set(r, clean(value))
        return true
    } catch (e) {
        console.warn(`War archive write skipped (${path}):`, e?.message ?? e)
        return false
    }
}

export async function archiveMeta(gameData, meta) {
    const base = archivePath(gameData)
    if (!base) return false
    return safeSet(`${base}/meta`, meta)
}

export async function archiveInitialHomefront(gameData, side, serializedHf) {
    const base = archivePath(gameData)
    if (!base) return false
    return safeSet(`${base}/initialHomefronts/${side}`, serializedHf)
}

export async function archiveRound(gameData, entry) {
    const base = archivePath(gameData)
    if (!base || entry?.round == null) return false
    return safeSet(`${base}/rounds/${entry.round}`, entry)
}

// The first result written wins: a completed war can't be relabelled
// abandoned by the loser's Main Menu click, and the second client's
// identical completion write is a no-op.
export async function archiveResult(gameData, result) {
    const base = archivePath(gameData)
    if (!base) return false
    return safeSet(`${base}/result`, result, { onlyIfMissing: true })
}
